/* eslint-disable no-template-curly-in-string */
import React from 'react';
import * as Yup from 'yup';
import { navigate } from 'gatsby';

import { consistentString } from '../../utils/content-formatting';
import { sendEvent } from '../../utils/analytics';
import { getQueryParams } from '../../utils/query-params';
import { isBrowser } from '../../utils/is-browser';
import { getAnalyticsParams } from '../../utils/session-storage';
import { labels } from './strings';
import RichText from '../rich-text';
import fieldTypes from './field-types';
import validations from './validations';
import initialValues from './initial-values';

export const shouldReveal = (
  values,
  { name, revealValues, revealFields, type }
) => {
  if (!revealValues || !revealFields) return false;

  const fieldValue = values[name];
  const mappedRevealLabels = revealValues.map(({ label }) => label);

  // handles cases where checkbox fields have only one checkbox and their value is treated as a boolean
  if (type === fieldTypes.CHECKBOXES && fieldValue === true) return true;

  return Array.isArray(fieldValue)
    ? fieldValue.some((value) => mappedRevealLabels.includes(value))
    : mappedRevealLabels.includes(fieldValue);
};

export const getInitialValue = (field) => {
  const queryParams = isBrowser && getQueryParams(window.location.search);

  if (field.assembly || field.richText) {
    return;
  }

  // If the field name matches one of the query params, get the initial value for that field from the query params
  if (queryParams[field.name]) {
    return queryParams[field.name];
  }

  if (initialValues[field.type]) {
    return initialValues[field.type](field);
  }

  return field.defaultValue || '';
};

export const getInitialValues = (fields, captcha) => {
  const values = {};

  if (captcha) {
    values.captcha = {};
  }

  if (!fields) {
    return values;
  }

  // Loop through all fields to build an object to store initial values to pass to formik
  fields.forEach((field) => {
    if (field.fieldset) {
      field.fields
        .filter(({ assembly, richText }) => !assembly && !richText)
        .forEach((fieldSetField) => {
          values[fieldSetField.name] = getInitialValue(fieldSetField);
        });
    } else {
      values[field.name] = getInitialValue(field);

      if (field.revealFields) {
        field.revealFields
          .filter(({ assembly, richText }) => !assembly && !richText)
          .forEach((revealField) => {
            values[revealField.name] = getInitialValue(revealField);

            // For nested reveal fields, loop through them and add them to the initial values object
            if (revealField.revealFields) {
              revealField.revealFields
                .filter(({ assembly, richText }) => !assembly && !richText)
                .forEach((nestedRevealField) => {
                  values[nestedRevealField.name] =
                    getInitialValue(nestedRevealField);
                });
            }
          });
      }
    }
  });
  return values;
};

export const getFieldValidation = (field) => {
  if (!field.type) return;
  const fieldType = consistentString(field.type);
  const fieldLabel = (field.fieldLabel || field.label || '').toLowerCase();

  if (validations[fieldType]) {
    // Apply a validation type to each field
    return validations[fieldType](field.required, fieldLabel, field);
  }

  return Yup.string();
};

export const getValidationSchema = (fields) =>
  Yup.lazy((values) => {
    const validationSchema = {};

    if (!fields) {
      return validationSchema;
    }

    // Loop through all form fields and build a Yup validationSchema object for formik
    fields.forEach((field) => {
      validationSchema[field.name] = getFieldValidation(field);

      if (shouldReveal(values, field)) {
        field.revealFields.forEach((revealField) => {
          validationSchema[revealField.name] = getFieldValidation(revealField);

          // For nested reveal fields, loop through them and add them to the validation schema
          if (shouldReveal(values, revealField)) {
            revealField.revealFields.forEach((nestedRevealField) => {
              validationSchema[nestedRevealField.name] =
                getFieldValidation(nestedRevealField);
            });
          }
        });
      }
    });

    return Yup.object().shape(validationSchema);
  });

export const sendAnalyticsSubmissionEvent = (
  formValues,
  formFields,
  hiddenInitialValues,
  extraFields
) => {
  // Get any fields that are marked to send to analytics and set bool
  const fieldsToSend = formFields.reduce((acc, field) => {
    if (!field.options) return acc;
    if (!field.options.some((option) => option.sendToAnalytics)) {
      return acc;
    }

    if (field.options.length === 1) {
      // If only one option
      const option = field.options[0];
      const valueBool = formFields[field.name] === option.value;
      acc[option.value] = valueBool;
    } else {
      // If multiple options check if the option was selected
      field.options.forEach((option) => {
        if (option.sendToAnalytics) {
          // Check if the field had a value
          const valueBool = formValues[field.name].includes(option.value);

          acc[option.value] = valueBool;
          return acc;
        }
      });
    }
    return acc;
  }, {});

  const analyticsObejctToSend = {
    event: 'formSubmitted',
    ...hiddenInitialValues,
    ...fieldsToSend,
    ...extraFields,
  };

  sendEvent(analyticsObejctToSend);
};

/**
 * Infers which types of consent are needed based on which form fields
 * are present in a form configured in contentful.
 *
 * This assumes some field naming conventions.
 */
export function inferConsentTypes(formFields) {
  const types = [];

  if (formFields.some(({ type }) => type === fieldTypes.EMAIL)) {
    types.push({ value: 'email', label: labels.CONSENT_EMAIL });
  }

  if (formFields.some(({ type }) => type === fieldTypes.PHONE_NUMBER)) {
    types.push(
      { value: 'text', label: labels.CONSENT_TEXT },
      { value: 'phone', label: labels.CONSENT_PHONE }
    );
  }

  if (formFields.some(({ type }) => type === fieldTypes.ADDRESS)) {
    types.push({ value: 'post', label: labels.CONSENT_POST, reversed: true });
  }

  return types;
}

export const buildFieldsFromContentfulData = (fields) =>
  fields &&
  fields.map(
    ({
      id,
      name,
      type,
      label,
      description,
      fieldsetLegend,
      fieldsetDescription,
      formFields,
      helpText,
      defaultValue,
      labelSize,
      fieldWidth,
      options,
      internal,
      optional,
      revealValues,
      revealFields,
      ...rest
    }) => {
      switch (internal.type) {
        case 'ContentfulComponentFormField':
        case 'ContentfulComponentFormFieldOptional':
          return {
            name,
            type: consistentString(type),
            label,
            description,
            labelSize: labelSize ? consistentString(labelSize) : 'standard',
            short: consistentString(fieldWidth) === 'small',
            required: !optional,
            helpText,
            defaultValue,
            options:
              options &&
              (options.length === 1
                ? [
                    {
                      label: options[0].label,
                      value: true,
                      content: options[0].content && (
                        <RichText richText={options[0].content} />
                      ),
                    },
                  ]
                : options.map((option) => ({
                    value: option.value,
                    label: option.label || option.value,
                    content: option.content && (
                      <RichText richText={option.content} />
                    ),
                  }))),
            revealValues,
            revealFields: buildFieldsFromContentfulData(revealFields),
          };
        case 'ContentfulComponentFormFieldset':
          return {
            fieldset: true,
            legend: fieldsetLegend,
            description: fieldsetDescription.fieldsetDescription,
            fields: buildFieldsFromContentfulData(formFields),
          };
        case 'ContentfulComponentSimpleRichTextBlock':
          return {
            richText: true,
            id,
            ...rest,
          };
        case 'ContentfulComponentFormUploadField':
          return {
            name,
            type: 'file-upload',
            label,
            description,
            helpText,
            required: !optional,
            ...rest,
          };
        /**
         * This is not exected to run, as only the above fields are embeddable
         * in forms.
         */
        default:
          return {
            internal,
            id,
            ...rest,
          };
      }
    }
  );

export const determinePostSubmissionJourney = (
  event,
  values,
  redirectAfterSubmission
) => {
  if (event && consistentString(event.eventStatus) === 'open') {
    const eventRegLink = event.link[0];

    const isIraiserLink = /donate.shelter.org.uk/.test(eventRegLink.URL);

    if (!isIraiserLink) return window.location.assign(eventRegLink.URL);

    const { firstName: firstname, lastName: lastname, email, address } = values;

    const { utmParams, gclid } = getAnalyticsParams();

    const utmParamsFormatted = {};

    if (utmParams) {
      Object.entries(utmParams).forEach(([key, value]) => {
        utmParamsFormatted[`reserved_${key}`] = value;
      });
    }

    const params = {
      firstname,
      lastname,
      email,
      ...address,
      amount: event.registrationFee * 100,
      free_amount: 1,
      ...utmParamsFormatted,
    };

    if (gclid !== undefined) {
      params.reserved_gclid = gclid;
    }

    const paramString = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join('&');

    const separator = eventRegLink.URL.includes('?') ? '&' : '?';

    return window.location.assign(
      `${eventRegLink.URL}${separator}${paramString}`
    );
  }

  const linkType = redirectAfterSubmission[0].internal.type;

  if (linkType === 'ContentfulComponentExternalLink') {
    return window.location.assign(redirectAfterSubmission[0].URL);
  }

  return navigate(redirectAfterSubmission[0].navigation.path);
};

export const generateSteps = (fields) => {
  if (!Array.isArray(fields)) return [[]];

  let stepCount = 0;

  // remove the first and/or last fields from the array if they are of type "section-break"
  while (fields[0]?.type === fieldTypes.SECTION_BREAK) fields.shift();
  while (fields[fields.length - 1]?.type === fieldTypes.SECTION_BREAK) {
    fields.pop();
  }

  return fields.reduce(
    (acc, field) => {
      if (field.type === fieldTypes.SECTION_BREAK) {
        stepCount += 1;
        return [...acc, []];
      }

      acc[stepCount] = [...acc[stepCount], field];
      return acc;
    },
    [[]]
  );
};

// Removes any key/value pairs from the Formik values object for fields which have been hidden, ready to send to the backend API
export const sanitiseValues = (values, fields) => {
  const sanitisedValues = values;
  fields.forEach((field) => {
    if (!shouldReveal(values, field)) {
      field.revealFields?.forEach(({ name }) => {
        delete sanitisedValues[name];
      });
    }
  });

  return sanitisedValues;
};
